import React from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, removeFromCart } from "../../actions/cartActions";
import { getUserFromLocalStorage } from "utilities/user";
import AppSelect from "../../components/ui/AppSelect";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:        "#f4f6fb",
  panel:     "#ffffff",
  header:    "#1a2b4a",
  blue:      "#2c7be5",
  blueSoft:  "#eaf2ff",
  teal:      "#00b69b",
  tealSoft:  "#e6faf7",
  red:       "#e63757",
  redSoft:   "#fff0f3",
  green:     "#0ab39c",
  text:      "#1a2b4a",
  textMid:   "#4a5568",
  textLight: "#8898aa",
  border:    "#e2e8f0",
  shadow:    "0 2px 12px rgba(26,43,74,0.08)",
  radius:    "10px",
  radiusSm:  "6px",
};

const fmt = (n) =>
  Number(n).toLocaleString("en-UG", { minimumFractionDigits: 0 });

// ── Skeleton row ──────────────────────────────────────────────────────────────
const SkeletonItem = () => (
  <div style={{
    display: "flex", alignItems: "center", gap: 14,
    padding: "14px 20px", borderBottom: `1px solid ${C.border}`,
  }}>
    <div style={{ width: 80, height: 80, borderRadius: C.radiusSm, flexShrink: 0, ...shimStyle }} />
    <div style={{ flex: 1 }}>
      <div style={{ height: 13, width: "55%", borderRadius: 4, marginBottom: 8, ...shimStyle }} />
      <div style={{ height: 11, width: "30%", borderRadius: 4, ...shimStyle }} />
    </div>
    <div style={{ width: 70, height: 32, borderRadius: C.radiusSm, ...shimStyle }} />
    <div style={{ width: 32, height: 32, borderRadius: C.radiusSm, ...shimStyle }} />
  </div>
);

const shimStyle = {
  background: "linear-gradient(90deg,#e8edf5 25%,#f4f6fb 50%,#e8edf5 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.4s infinite",
};

const CartScreen = ({ location, history }) => {
  const user    = getUserFromLocalStorage();
  const dispatch = useDispatch();
  const { cartItems, isLoading } = useSelector((state) => state.cart);

  const totalQty   = cartItems.reduce((acc, item) => acc + Number(item.qty), 0);
  const totalPrice = cartItems.reduce((acc, item) => acc + Number(item.qty) * Number(item.price), 0);

  const removeFromCartHandler = (id) => dispatch(removeFromCart(id));
  const checkoutHandler       = () => history.push("/admin/shipping");
  const addMoreProducts       = (e) => { e.preventDefault(); history.push("/admin/shopCards"); };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "28px 16px 56px" }}>
      <div style={{ maxWidth: 1060, margin: "0 auto" }}>

        {/* ── Header ── */}
        <div style={{
          background: `linear-gradient(135deg, ${C.header} 0%, #243b6e 100%)`,
          borderRadius: C.radius, padding: "20px 28px", marginBottom: 24,
          boxShadow: "0 4px 24px rgba(26,43,74,0.18)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 42, height: 42, borderRadius: C.radiusSm, flexShrink: 0,
              background: "rgba(44,123,229,0.2)", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="fas fa-shopping-cart" style={{ color: "#7eb8f7", fontSize: 17 }} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", letterSpacing: "-0.3px", marginBottom: 2 }}>
                Shopping Cart
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                {isLoading ? "Loading…" : `${totalQty} item${totalQty !== 1 ? "s" : ""} in your cart`}
              </div>
            </div>
          </div>
          <button
            onClick={addMoreProducts}
            style={{
              background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: C.radiusSm, padding: "7px 16px",
              color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <i className="fas fa-plus" style={{ fontSize: 10 }} />
            Add More Items
          </button>
        </div>

        {/* ── Two-column layout ── */}
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>

          {/* ── Left: cart items ── */}
          <div style={{ flex: "1 1 560px", minWidth: 0 }}>
            <div style={{
              background: C.panel, borderRadius: C.radius,
              border: `1px solid ${C.border}`, boxShadow: C.shadow, overflow: "hidden",
            }}>

              {/* Loading skeleton */}
              {isLoading && (
                <>
                  {[1, 2, 3].map((i) => <SkeletonItem key={i} />)}
                </>
              )}

              {/* Empty state */}
              {!isLoading && cartItems.length === 0 && (
                <div style={{
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  padding: "64px 24px",
                }}>
                  <div style={{
                    width: 72, height: 72, borderRadius: "50%",
                    background: C.blueSoft, display: "flex", alignItems: "center", justifyContent: "center",
                    marginBottom: 18,
                  }}>
                    <i className="fas fa-shopping-cart" style={{ color: C.blue, fontSize: 28 }} />
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                    Your cart is empty
                  </div>
                  <div style={{ fontSize: 13, color: C.textLight, marginBottom: 20, textAlign: "center", maxWidth: 280 }}>
                    Looks like you haven't added anything yet.
                  </div>
                  <Link
                    to="/admin/shopCards"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 7,
                      padding: "9px 22px", borderRadius: C.radiusSm,
                      background: `linear-gradient(135deg, ${C.blue} 0%, #1a5fc4 100%)`,
                      color: "#fff", fontSize: 13, fontWeight: 700,
                      textDecoration: "none", boxShadow: "0 2px 8px rgba(44,123,229,0.3)",
                    }}
                  >
                    <i className="fas fa-store" style={{ fontSize: 11 }} />
                    Browse Products
                  </Link>
                </div>
              )}

              {/* Cart item rows */}
              {!isLoading && cartItems.length > 0 && cartItems.map((item, idx) => (
                <div
                  key={item._id}
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "14px 20px",
                    borderBottom: idx < cartItems.length - 1 ? `1px solid ${C.border}` : "none",
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.bg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {/* Thumbnail */}
                  <div style={{
                    width: 80, height: 80, borderRadius: C.radiusSm, flexShrink: 0,
                    overflow: "hidden", border: `1px solid ${C.border}`, background: C.bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <i className="fas fa-image" style={{ color: C.textLight, fontSize: 20 }} />
                    )}
                  </div>

                  {/* Name + unit */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link
                      to={`/admin/listing-details/${item._id}`}
                      style={{
                        display: "block", fontWeight: 700, fontSize: 13, color: C.text,
                        textDecoration: "none", marginBottom: 3,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}
                    >
                      {item.name}
                    </Link>
                    <div style={{ fontSize: 11, color: C.textLight }}>
                      {item.unit || "unit"} &nbsp;·&nbsp;
                      <span style={{ fontWeight: 700, color: C.textMid }}>
                        UGX {fmt(item.price)}
                      </span>
                    </div>
                  </div>

                  {/* Qty selector */}
                  <div style={{ flexShrink: 0 }}>
                    <AppSelect
                      value={item.qty}
                      onChange={(e) =>
                        dispatch(addToCart(item._id, Number(e.target.value), item.vendorId, item.unit))
                      }
                      style={{
                        padding: "5px 8px", borderRadius: C.radiusSm,
                        border: `1px solid ${C.border}`, background: C.bg,
                        color: C.text, fontSize: 13, fontWeight: 600,
                        cursor: "pointer", outline: "none",
                      }}
                    >
                      {[...Array(Math.max(item.countInStock || 1, item.qty)).keys()].map((x) => (
                        <option key={x + 1} value={x + 1}>{x + 1}</option>
                      ))}
                    </AppSelect>
                  </div>

                  {/* Line total */}
                  <div style={{
                    flexShrink: 0, minWidth: 80, textAlign: "right",
                    fontSize: 13, fontWeight: 800, color: C.text,
                  }}>
                    UGX {fmt(Number(item.qty) * Number(item.price))}
                  </div>

                  {/* Remove */}
                  <button
                    title="Remove from cart"
                    onClick={() => removeFromCartHandler(item._id)}
                    style={{
                      flexShrink: 0, width: 32, height: 32, borderRadius: C.radiusSm,
                      border: `1px solid ${C.border}`, background: C.panel,
                      color: C.textLight, fontSize: 12, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.12s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = C.redSoft;
                      e.currentTarget.style.borderColor = C.red;
                      e.currentTarget.style.color = C.red;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = C.panel;
                      e.currentTarget.style.borderColor = C.border;
                      e.currentTarget.style.color = C.textLight;
                    }}
                  >
                    <i className="fas fa-trash-alt" />
                  </button>
                </div>
              ))}

              {/* Add more footer */}
              {!isLoading && cartItems.length > 0 && (
                <div style={{
                  borderTop: `1px solid ${C.border}`, padding: "12px 20px",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <span style={{ fontSize: 12, color: C.textLight }}>
                    {cartItems.length} product{cartItems.length !== 1 ? "s" : ""} in cart
                  </span>
                  <button
                    onClick={addMoreProducts}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "6px 14px", borderRadius: C.radiusSm,
                      border: `1px solid ${C.border}`, background: C.panel,
                      color: C.textMid, fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    <i className="fas fa-plus-circle" style={{ fontSize: 10, color: C.blue }} />
                    Add More Items
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Right: order summary ── */}
          <div style={{ flex: "0 0 300px", minWidth: 260 }}>
            <div style={{
              background: C.panel, borderRadius: C.radius,
              border: `1px solid ${C.border}`, boxShadow: C.shadow, overflow: "hidden",
            }}>
              {/* Summary header */}
              <div style={{
                padding: "13px 20px", borderBottom: `1px solid ${C.border}`,
                background: "#fafbfd",
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <i className="fas fa-receipt" style={{ color: C.blue, fontSize: 12 }} />
                <span style={{
                  fontSize: 11, fontWeight: 700, color: C.textMid,
                  textTransform: "uppercase", letterSpacing: "0.07em",
                }}>
                  Order Summary
                </span>
              </div>

              <div style={{ padding: "18px 20px" }}>
                {/* Line items summary */}
                <div style={{ marginBottom: 16 }}>
                  {cartItems.map((item) => (
                    <div key={item._id} style={{
                      display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 8,
                      fontSize: 12, color: C.textMid,
                    }}>
                      <span style={{
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        maxWidth: 160, fontWeight: 500,
                      }}>
                        {item.name} <span style={{ color: C.textLight }}>× {item.qty}</span>
                      </span>
                      <span style={{ fontWeight: 700, flexShrink: 0, marginLeft: 8 }}>
                        UGX {fmt(Number(item.qty) * Number(item.price))}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Divider */}
                <div style={{ borderTop: `1px dashed ${C.border}`, marginBottom: 14 }} />

                {/* Total */}
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  marginBottom: 6,
                }}>
                  <span style={{ fontSize: 12, color: C.textLight }}>
                    Total items
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                    {totalQty}
                  </span>
                </div>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  marginBottom: 20,
                }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Total</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
                    UGX {fmt(totalPrice)}
                  </span>
                </div>

                {/* Checkout CTA */}
                <button
                  type="button"
                  disabled={cartItems.length === 0}
                  onClick={checkoutHandler}
                  style={{
                    width: "100%", padding: "11px 0", borderRadius: C.radiusSm,
                    border: "none", cursor: cartItems.length === 0 ? "not-allowed" : "pointer",
                    background: cartItems.length === 0
                      ? C.border
                      : `linear-gradient(135deg, ${C.teal} 0%, #008f7a 100%)`,
                    color: cartItems.length === 0 ? C.textLight : "#fff",
                    fontSize: 14, fontWeight: 700,
                    boxShadow: cartItems.length === 0 ? "none" : "0 3px 10px rgba(0,182,155,0.3)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    transition: "opacity 0.15s",
                  }}
                  onMouseEnter={(e) => { if (cartItems.length > 0) e.currentTarget.style.opacity = "0.88"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                >
                  <i className="fas fa-lock" style={{ fontSize: 12 }} />
                  Proceed to Checkout
                </button>

                <div style={{
                  marginTop: 12, textAlign: "center",
                  fontSize: 11, color: C.textLight,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                }}>
                  <i className="fas fa-shield-alt" style={{ fontSize: 10, color: C.teal }} />
                  Secure checkout
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
      `}</style>
    </div>
  );
};

export default CartScreen;
